// Bot jako warstwa w zadaniach (SPEC §14.5): opis tego, co dziecko robi i jak mu poszło, wysyłany do /api/chat jako `context`.
// Czysta logika bez DOM i sieci — używają jej ekran (src/ui/companion.ts), serwer (walidacja) i bench.
// Zasada: ocenę (zaliczone / niezaliczone, gwiazdki) robi kod; model dostaje WYNIK jako fakt i pisze tylko krótką reakcję albo odpowiedź na pytanie.
import { config } from '../config.ts';
import { stripTags } from './text.ts';
import type { BuildTask, ClozeTask, Outcome, SpeakTask, Task, Verdict } from '../types.ts';

/** Co dziecko robi w tej chwili. */
export interface CoachTask {
  kind: 'speak' | 'cloze' | 'build' | 'banter';
  phase?: 'repeat' | 'read' | 'build';
  /** Zdanie (albo słowo) po angielsku, nad którym dziecko pracuje. */
  text?: string;
  /** Polskie tłumaczenie tego zdania. */
  pl?: string;
  /** Słówko zadania (karta, wpisywanie). */
  word?: string;
  wordPl?: string;
}

/** Jak poszła jedna próba (fakty z oceny, nie opinia modelu). */
export interface CoachAttempt {
  /** Która to próba i ile jest dozwolonych. */
  no: number;
  of: number;
  passed: boolean;
  reason?: Verdict['reason'];
  stars?: 1 | 2 | 3;
  /** Procent wymowy zdania (tylko gdy oceniał Azure). */
  percent?: number;
  /** Co usłyszało rozpoznawanie (może zawierać błędy rozpoznania). */
  heard?: string;
  /** Słowa słabo wypowiedziane i słowa pominięte. */
  weak?: string[];
  missing?: string[];
  /** Co dziecko wpisało (zadanie „wpisz słowo”). */
  typed?: string;
}

export interface CoachSummary {
  mode: 'lesson' | 'review';
  sentences: number;
  stars: number;
  maxStars: number;
  percent?: number;
  /** Zdania, które minęły bez zaliczenia (tekst po angielsku). */
  unpassed: string[];
  clozeOk: number;
  clozeTotal: number;
}

export type CoachContext =
  | { kind: 'ask'; task: CoachTask }
  | { kind: 'react'; task: CoachTask; attempt: CoachAttempt }
  | { kind: 'summary'; summary: CoachSummary };

export type CoachKind = CoachContext['kind'];

// --- opis zadania -----------------------------------------------------------------------------------

export function taskInfoOf(task: Task): CoachTask {
  switch (task.kind) {
    case 'speak': {
      const t = task as SpeakTask;
      return { kind: 'speak', phase: t.phase === 'scene' ? 'read' : t.phase, text: t.text, pl: t.pl };
    }
    case 'cloze': {
      const t = task as ClozeTask;
      return { kind: 'cloze', word: t.word.word, wordPl: t.word.pl, text: t.sentence, pl: t.pl };
    }
    case 'build': {
      const t = task as BuildTask;
      return { kind: 'build', text: t.text, pl: t.pl };
    }
    case 'banter': {
      const text = task.line.lang === 'en' ? stripTags(task.line.text) : '';
      return { kind: 'banter', ...(text ? { text } : {}), ...(task.line.pl ? { pl: task.line.pl } : {}) };
    }
    // Kurs v2 działa bez bota (warstwa AI wstrzymana), więc te opisy służą tylko wyczerpaniu typów: najbliższe rodzaje zadań.
    case 'quiz':
      return { kind: 'cloze', word: task.word, text: task.q, pl: task.pl };
    case 'say':
      return { kind: 'speak', phase: 'build', ...(task.accept[0] ? { text: task.accept[0] } : {}), pl: task.situationPl };
    case 'grammar':
      return { kind: 'banter', text: task.card.note.title };
  }
}

// --- polityka reakcji -------------------------------------------------------------------------------

export type ReactOnPass = 'never' | 'notable' | 'always';

/**
 * Czy po tej próbie bot ma dołożyć własną reakcję (zapytanie do modelu).
 * Po niezaliczonej próbie — zawsze (podpowiedź do słowa zamiast gotowej linijki). Po zaliczonej: `notable` = gdy nie było 3★ za pierwszym razem
 * albo wskazano słabe słowo; czysty sukces za pierwszym razem zostaje przy gotowej pochwale z nagraniem (bez opóźnienia).
 */
export function shouldReact(a: { passed: boolean; stars?: number; no: number; weak?: readonly string[] }, onPass: ReactOnPass = config.coach.reactOnPass): boolean {
  if (!a.passed) return true;
  if (onPass === 'always') return true;
  if (onPass === 'never') return false;
  return (a.stars ?? 3) < 3 || a.no > 1 || (a.weak?.length ?? 0) > 0;
}

/** Fakty z werdyktu zadania „powiedz zdanie”. */
export function speakAttempt(a: { no: number; verdict: Verdict; percent?: number; heard?: string }): CoachAttempt {
  const v = a.verdict;
  return {
    no: a.no,
    of: config.scoring.maxAttempts,
    passed: v.passed,
    reason: v.reason,
    stars: v.stars,
    ...(a.percent !== undefined ? { percent: a.percent } : {}),
    ...(a.heard ? { heard: a.heard } : {}),
    ...(v.weakWords.length > 0 ? { weak: v.weakWords } : {}),
    ...(v.missing.length > 0 ? { missing: v.missing } : {}),
  };
}

export function typedAttempt(a: { no: number; passed: boolean; typed?: string }): CoachAttempt {
  return { no: a.no, of: config.session.fixAfterAttempts, passed: a.passed, ...(a.typed ? { typed: a.typed } : {}) };
}

// --- podsumowanie sesji -----------------------------------------------------------------------------

export function summaryInfo(a: { mode: 'lesson' | 'review'; outcomes: readonly Outcome[]; percent: number | null; sentenceText: (id: string) => string }): CoachSummary {
  const speak = a.outcomes.filter((o) => o.kind === 'speak');
  const cloze = a.outcomes.filter((o) => o.kind === 'cloze');
  return {
    mode: a.mode,
    sentences: speak.length,
    stars: speak.reduce((n, o) => n + (o.stars ?? 0), 0),
    maxStars: speak.length * 3,
    ...(a.percent !== null ? { percent: a.percent } : {}),
    unpassed: [...new Set(speak.filter((o) => o.passed === false).map((o) => a.sentenceText(o.ref)))],
    clozeOk: cloze.filter((o) => o.correct).length,
    clozeTotal: cloze.length,
  };
}

// --- zapis w dzienniku -------------------------------------------------------------------------------

/** Krótki opis sytuacji do dziennika rozmów (do wklejenia w rozmowie z Claude przy strojeniu). */
export function describeContext(c: CoachContext): string {
  if (c.kind === 'summary') {
    const s = c.summary;
    return `summary ${s.mode} ★${s.stars}/${s.maxStars}${s.percent !== undefined ? ` ${s.percent}%` : ''}${s.unpassed.length ? ` unpassed:${s.unpassed.join('|')}` : ''} type:${s.clozeOk}/${s.clozeTotal}`;
  }
  const t = c.task;
  const where = `${t.kind}${t.phase ? `/${t.phase}` : ''}${t.text ? ` "${t.text}"` : t.word ? ` "${t.word}"` : ''}`;
  if (c.kind === 'ask') return `ask ${where}`;
  const a = c.attempt;
  const bits = [`${a.no}/${a.of}`, a.passed ? 'pass' : 'fail', a.reason ?? '', a.stars ? `★${a.stars}` : '', a.percent !== undefined ? `${a.percent}%` : '', a.heard ? `heard:"${a.heard}"` : '', a.typed ? `typed:"${a.typed}"` : '', a.weak?.length ? `weak:${a.weak.join(',')}` : '', a.missing?.length ? `missing:${a.missing.join(',')}` : ''];
  return `react ${where} ${bits.filter(Boolean).join(' ')}`;
}
